import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Loader2, Building2, ExternalLink } from "lucide-react";
import { C, Panel, Empty, NotConnectedNote } from "./seoUi";
import { errMsg } from "@/lib/api";

const empty = {
  name: "", website: "", territory_label: "", service_lane: "", observed_strengths: "",
  observed_weaknesses: "", keyword_overlap_notes: "", backlink_notes: "", notes: "", franchise_id: "",
};

export default function CompetitorsPanel({ franchise, meta }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seo-intelligence/competitors", { params: franchise ? { franchise } : {} });
      setItems(data.items || []); setNote(data.note || "");
    } catch { toast.error("Could not load competitors."); }
    finally { setLoading(false); }
  }, [franchise]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.name.trim()) { toast.error("Competitor name is required."); return; }
    setSaving(true);
    try {
      await api.post("/seo-intelligence/competitors", { ...form, franchise_id: form.franchise_id || (franchise || null) });
      toast.success("Competitor saved."); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this competitor?")) return;
    try { await api.delete(`/seo-intelligence/competitors/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Removed."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Panel title="Competitors" testid="seo-tab-competitors"
      subtitle="Manual notes only — no competitor rankings, traffic, or backlinks are fetched or fabricated."
      right={<Button data-testid="add-competitor-btn" size="sm" onClick={() => setOpen(true)} className="h-9 gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white"><Plus size={14} /> Add competitor</Button>}>
      <NotConnectedNote>{note || "Competitor intelligence will populate automatically once Semrush/Ahrefs is connected."}</NotConnectedNote>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" style={{ color: C.navy }} /></div> : items.length ? (
        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          {items.map((c, i) => (
            <div key={c.id} data-testid={`competitor-card-${i}`} className="rounded-[0.9rem] border bg-white p-4" style={{ borderColor: C.line }}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1.5"><Building2 size={15} style={{ color: C.navy }} /><span className="font-display font-semibold text-[15px]" style={{ color: C.navy }}>{c.name}</span></div>
                  {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-[12px] inline-flex items-center gap-1 mt-0.5" style={{ color: C.sky }}>{c.website} <ExternalLink size={10} /></a>}
                  {c.territory_label && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{c.territory_label}</div>}
                </div>
                <button data-testid={`competitor-del-${i}`} onClick={() => del(c.id)} className="text-[#9C1E22] hover:opacity-70"><Trash2 size={15} /></button>
              </div>
              {c.observed_strengths && <p className="text-[12.5px] mt-2"><b style={{ color: C.green }}>Strengths:</b> {c.observed_strengths}</p>}
              {c.observed_weaknesses && <p className="text-[12.5px] mt-1"><b style={{ color: C.amber }}>Weaknesses:</b> {c.observed_weaknesses}</p>}
              {c.keyword_overlap_notes && <p className="text-[12.5px] mt-1"><b>Keyword overlap:</b> {c.keyword_overlap_notes}</p>}
              {c.notes && <p className="text-[12px] mt-1" style={{ color: C.slate }}>{c.notes}</p>}
            </div>
          ))}
        </div>
      ) : <Empty>No competitors yet. Add local competitors you want to keep an eye on.</Empty>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="competitor-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Add competitor</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input data-testid="comp-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="space-y-1"><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" /></div>
            </div>
            <div className="space-y-1"><Label>Territory / market</Label><Input value={form.territory_label} onChange={(e) => set("territory_label", e.target.value)} placeholder="e.g. Kansas City" /></div>
            {meta?.is_super && (meta?.franchises || []).length > 0 && (
              <div className="space-y-1"><Label>Link to territory (optional)</Label>
                <Select value={form.franchise_id} onValueChange={(v) => set("franchise_id", v)}>
                  <SelectTrigger data-testid="comp-franchise"><SelectValue placeholder="Global" /></SelectTrigger>
                  <SelectContent>{meta.franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1"><Label>Observed strengths</Label><Textarea value={form.observed_strengths} onChange={(e) => set("observed_strengths", e.target.value)} rows={2} /></div>
            <div className="space-y-1"><Label>Observed weaknesses</Label><Textarea value={form.observed_weaknesses} onChange={(e) => set("observed_weaknesses", e.target.value)} rows={2} /></div>
            <div className="space-y-1"><Label>Keyword overlap notes</Label><Textarea value={form.keyword_overlap_notes} onChange={(e) => set("keyword_overlap_notes", e.target.value)} rows={2} /></div>
            <div className="space-y-1"><Label>General notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="comp-save" disabled={saving} onClick={create} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 className="animate-spin" size={14} />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
